import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// Shared baseline only — ADD this app's own module status enums here (see
// govfin-portal/src/ui/components.tsx for the pattern: one entry per status
// string your backend APIs actually return). Unmapped statuses still render,
// just in the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DONE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',

  // StaffDesk-specific additions (c13/c5/c4/c7/c3/c9 status enums not covered above):
  LEFT: 'bg-slate-100 text-slate-500 ring-slate-200', // Officer.status
  INACTIVE: 'bg-slate-100 text-slate-500 ring-slate-200', // FlowDefinition.active=false
  AWAITING_PAYMENT: 'bg-amber-50 text-amber-700 ring-amber-200', // Case.state
  NONE: 'bg-slate-100 text-slate-500 ring-slate-200', // Case.feeStatus
  BEFORE_TRIBUNAL: 'bg-violet-50 text-violet-700 ring-violet-200', // Appeal.state
  UPHELD: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // Appeal.state
  OVERTURNED: 'bg-rose-50 text-rose-700 ring-rose-200', // Appeal.state
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200', // Appeal.state
  MET: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // ChecklistItem.status
  WAIVED: 'bg-amber-50 text-amber-700 ring-amber-200', // ChecklistItem.status
  SENT: 'bg-sky-50 text-sky-700 ring-sky-200', // Referral.status
  RESPONDED: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // Referral.status
  OVERDUE: 'bg-rose-50 text-rose-700 ring-rose-200', // Referral.status
  SCHEDULED: 'bg-blue-50 text-blue-700 ring-blue-200', // Broadcast.status
  DISPATCHED: 'bg-indigo-50 text-indigo-700 ring-indigo-200', // Broadcast/PostalTask.status
  PRINTED: 'bg-sky-50 text-sky-700 ring-sky-200', // PostalTask.status
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // PostalTask.status
  RETIRED: 'bg-slate-100 text-slate-500 ring-slate-200', // LegalBasis/Address
  VALID: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // DocVault verify result
  SUPERSEDED: 'bg-slate-100 text-slate-500 ring-slate-200', // DocVault verify result
  ASSIGNED: 'bg-emerald-50 text-emerald-700 ring-emerald-200', // Address.status
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
